/**
 * "Needed by" cue shared by the Release drawer and the board cards.
 * Warmer as the date nears: red when due today or overdue, amber within 2 days,
 * grey otherwise. Day maths are calendar days in the user's timezone.
 */

import type { TFunction } from 'i18next';
import { calendarDayNumber } from './ReleaseDrawer/timeline';

export type NeededByTone = 'overdue' | 'soon' | 'normal';

export interface NeededByInfo {
  /** Calendar days from today (negative = overdue) */
  days: number;
  /** Compact token: "2d", "Today", "-1d" */
  short: string;
  /** Spoken/long wording: "Needed in 3 days" */
  label: string;
  /** Short relative wording for rows with an absolute date: "in 2 days" / "1 day overdue" */
  relative: string;
  tone: NeededByTone;
}

/** Text colour per tone (drawer bar, on a light background). */
export const NEEDED_BY_TEXT_CLASS: Record<NeededByTone, string> = {
  overdue: 'text-red-700 font-semibold',
  soon: 'text-amber-800 font-semibold',
  normal: 'text-gray-700',
};

/** Pill colours per tone (cards, sits on top of a thumbnail). */
export const NEEDED_BY_CHIP_CLASS: Record<NeededByTone, string> = {
  overdue: 'bg-red-50 text-red-700',
  soon: 'bg-amber-50 text-amber-800',
  normal: 'bg-gray-100 text-gray-700',
};

/** Planned calendar days may be stored as UTC midnight; that value names a day, not a local instant. */
export const isDateOnlyPlannedDate = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z)?$/.test(value);

export function getNeededByInfo(
  neededBy: string | null | undefined,
  timezone: string,
  t: TFunction<'common'>
): NeededByInfo | null {
  if (!neededBy) return null;
  const target = new Date(neededBy);
  if (Number.isNaN(target.getTime())) return null;

  const targetDay = calendarDayNumber(target, isDateOnlyPlannedDate(neededBy) ? 'UTC' : timezone);
  const days = targetDay - calendarDayNumber(new Date(), timezone);
  const short =
    days < 0
      ? `−${t('release_needed_short_days', { days: Math.abs(days) })}`
      : days === 0
        ? t('release_needed_short_today')
        : t('release_needed_short_days', { days });
  let label: string;
  if (days < 0) label = t('release_needed_ago', { count: Math.abs(days) });
  else if (days === 0) label = t('release_needed_today');
  else if (days === 1) label = t('release_needed_tomorrow');
  else label = t('release_needed_in_days', { days });

  let relative: string;
  if (days === -1) relative = t('release_needed_rel_overdue_one', { count: 1 });
  else if (days < -1) relative = t('release_needed_rel_overdue_other', { count: Math.abs(days) });
  else if (days === 0) relative = t('release_needed_rel_today');
  else if (days === 1) relative = t('release_needed_rel_tomorrow');
  else relative = t('release_needed_rel_in_days', { days });

  const tone: NeededByTone = days <= 0 ? 'overdue' : days <= 2 ? 'soon' : 'normal';
  return { days, short, label, relative, tone };
}
